import {
  CheckCircleFilled,
  CloseOutlined,
  PlusCircleFilled,
} from '@ant-design/icons'
import Icon from '@ant-design/icons/lib/components/Icon'
import { Button, Drawer, FormInstance, Space, message } from 'antd'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as PatientIcon } from '../../assets/Icons_ Patient Fill.svg'
import { ReactComponent as AlertIcon } from '../../assets/Icons_Alerts Fill.svg'
import { displayMessage } from '../../helpers/Message'
import { getDoctors } from '../../state/doctor/doctorReducer'
import { getNurses } from '../../state/nurse/nurseReducer'
import {
  errorPatient,
  selectErrorInPatient,
  selectSuccessInPatient,
  successPatient,
} from '../../state/patient/patientReducer'
import {
  errorPatientAlertConfiguration,
  selectErrorInPatientAlertConfiguration,
  selectSuccessInPatientAlertConfiguration,
  successPatientAlertConfiguration,
} from '../../state/patientAlertConfiguration/patientAlertConfigurationReducer'
import AlertConfigurationForm from '../Forms/AlertConfigurationForm/AlertConfigurationForm'
import PatientForm from '../Forms/PatientForm/PatientForm'
import {
  FooterDiv,
  FormContainer,
  StepTitle,
  StyledSteps,
} from './NewPatientViewStyle'

type Props = {
  open: boolean
  onClose: () => void
  patientForm: FormInstance<any>
  onFinishPatientDetails: () => void
  configureAlertForm: FormInstance<any>
  onFinishConfigureThreshold: () => void
}

const NewPatientView = ({
  onClose,
  open,
  patientForm,
  onFinishPatientDetails,
  onFinishConfigureThreshold,
  configureAlertForm,
}: Props) => {
  const [currentStep, setCurrentStep] = useState(0)
  const patientError = useSelector(selectErrorInPatient)
  const patientSuccess = useSelector(selectSuccessInPatient)
  const [patientLoading, setPatientLoading] = useState(false)
  const [alertConfigLoading, setAlertConfigLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getNurses())
    dispatch(getDoctors())
  }, [])

  const nextStep = async () => {
    setPatientLoading(true)
    patientForm.submit()
  }

  const onFinishFailedPatientForm = (val: any) => {
    setPatientLoading(false)
  }
  const onFinishFailedAlertConfigForm = (val: any) => {
    setAlertConfigLoading(false)
  }

  const registerEvent = () => {
    setAlertConfigLoading(true)

    const values = configureAlertForm.getFieldsValue()

    const {
      doctorNumber,
      escalationAlertNurseKey,
      firstAlertDoctorKey,
      firstAlertNurseKey,
      firstNumber,
      secondNumber,
    } = values

    if (
      !doctorNumber &&
      !escalationAlertNurseKey &&
      !firstAlertDoctorKey &&
      !firstAlertNurseKey &&
      !firstNumber &&
      !secondNumber
    ) {
      setAlertConfigLoading(false)
      onHandleClose()
      return
    }

    configureAlertForm.submit()
  }

  const onHandleClose = () => {
    onClose()
    setCurrentStep(0)
  }

  useEffect(() => {
    if (patientError) {
      setPatientLoading(false)
      dispatch(errorPatient(null))
    }
    if (patientSuccess) {
      setCurrentStep(1)
      setPatientLoading(false)
      dispatch(successPatient(null))
    }
  }, [patientError, patientSuccess])

  const alertConfigError = useSelector(selectErrorInPatientAlertConfiguration)
  const alertConfigSuccess = useSelector(
    selectSuccessInPatientAlertConfiguration
  )
  useEffect(() => {
    if (alertConfigError) {
      setAlertConfigLoading(false)
      displayMessage(alertConfigError, 'error', messageApi)
      dispatch(errorPatientAlertConfiguration(null))
    }
    if (alertConfigSuccess) {
      setAlertConfigLoading(false)
      patientForm.resetFields()
      configureAlertForm.resetFields()
      setCurrentStep(0)
      displayMessage(alertConfigSuccess, 'success', messageApi)
      dispatch(successPatientAlertConfiguration(null))
      onClose()
    }
  }, [alertConfigError, alertConfigSuccess])

  return (
    <>
      <Drawer
        title={
          <>
            <PlusCircleFilled style={{ fontSize: '24px' }} /> Register Patient{' '}
          </>
        }
        width='60vw'
        onClose={onHandleClose}
        closeIcon={false}
        open={open}
        extra={
          <Space>
            <Button shape='circle' type='text' onClick={onHandleClose}>
              <CloseOutlined />
            </Button>
          </Space>
        }
      >
        {contextHolder}
        <StyledSteps
          current={currentStep}
          items={[
            {
              title: <StepTitle>Patients</StepTitle>,
              icon: (
                <Icon style={{ fontSize: '25px' }} component={PatientIcon} />
              ),
            },
            {
              title: <StepTitle>Alert Configuration</StepTitle>,
              icon: <Icon style={{ fontSize: '25px' }} component={AlertIcon} />,
            },
          ]}
        />
        {currentStep === 0 && (
          <FormContainer>
            <PatientForm
              onFinishFailed={onFinishFailedPatientForm}
              onFinish={onFinishPatientDetails}
              disabledState={false}
              form={patientForm}
            />
          </FormContainer>
        )}
        {currentStep === 1 && (
          <AlertConfigurationForm
            onFinishFailed={onFinishFailedAlertConfigForm}
            onFinish={onFinishConfigureThreshold}
            disabledState={false}
            form={configureAlertForm}
          />
        )}
        <FooterDiv>
          {currentStep === 0 && (
            <Space>
              <Button onClick={onHandleClose}>Cancel</Button>
              <Button
                loading={patientLoading}
                icon={<CheckCircleFilled />}
                onClick={nextStep}
                type='primary'
              >
                Register
              </Button>
            </Space>
          )}
          {currentStep === 1 && (
            <Space>
              <Button
                loading={alertConfigLoading}
                icon={<CheckCircleFilled />}
                onClick={registerEvent}
                type='primary'
              >
                Save
              </Button>
            </Space>
          )}
        </FooterDiv>
      </Drawer>
    </>
  )
}

export default NewPatientView
